import { toast } from "sonner";
import type { BurnSlotResult } from "@workspace/api-client-react";

const MAX_LISTED = 6;

// Render up to MAX_LISTED "email → destination" lines, then a "+N more" tail so
// a big burn doesn't produce an unbounded toast.
function lines(items: string[]): string {
  if (items.length <= MAX_LISTED) return items.join("\n");
  const shown = items.slice(0, MAX_LISTED);
  return `${shown.join("\n")}\n+${items.length - MAX_LISTED} more`;
}

// Turn the account-level burn result into a clear operator summary toast:
// how many slots were burned, how many accounts were re-homed (and to WHICH
// new bank slot), and how many could not be re-homed because no open slot was
// available. The leftInPlace branch is retained for older results but is now
// normally empty, since burning re-homes every active account (incl.
// listed/sold).
export function showBurnSummary(res: BurnSlotResult): void {
  if (res.alreadyBurned) {
    toast.info("That bank account was already burned — nothing to do.");
    return;
  }

  const plural = (n: number, one: string, many: string) =>
    `${n} ${n === 1 ? one : many}`;

  const parts: string[] = [`Burned ${plural(res.slotsBurned, "slot", "slots")}`];
  if (res.rehomed.length) {
    parts.push(
      `re-homed ${plural(res.rehomed.length, "account", "accounts")} to new banks`,
    );
  }
  if (res.leftInPlace.length) {
    parts.push(
      `left ${plural(res.leftInPlace.length, "account", "accounts")} in place (already listed/sold)`,
    );
  }
  if (res.noSlotAvailableCount) {
    parts.push(
      `${plural(res.noSlotAvailableCount, "account", "accounts")} could not be re-homed (no open slots)`,
    );
  }

  // Spell out where each re-homed account landed so operators can verify the
  // swap moved them onto a DIFFERENT bank account.
  const descriptionParts: string[] = [];
  if (res.rehomed.length) {
    descriptionParts.push(
      "Moved to:\n" +
        lines(
          res.rehomed.map(
            (r) => `${r.email} → ${r.newSlot.bankAccountKey}-${r.newSlot.variant}`,
          ),
        ),
    );
  }
  if (res.noSlotAvailableCount) {
    descriptionParts.push(
      "No open slot for:\n" + lines(res.noSlotAvailableEmails),
    );
  }
  const description = descriptionParts.join("\n\n") || undefined;

  const message = parts.join(" · ");
  if (res.noSlotAvailableCount) {
    toast.warning(`${message}. Add a bank account or free a slot.`, {
      description,
    });
  } else {
    toast.success(message, { description });
  }
}
